// @ai-generated 2026-07-21T03:45:01+00:00 by philipp.kammerer@synqony.com using claude-fable-5
import {
    EnumNotificationModule,
    EnumNotificationRelatedObjectType,
    EnumPartnershipType,
    NotificationEmailTypeKeys,
    NotificationInternalTypeKeys,
    NotificationMandatoryInAppTypeKeys,
    NotificationPortalTypeKeys,
    NotificationTypeKeys,
    type ApiNotification,
    type ApiNotificationSetting,
} from '@Model';

type RouteBuilder = (id: string) => string;

/** The detail route per kind of record a notification points at, relative to the mandant subpath. */
const ROUTE_BY_RELATED_OBJECT_TYPE: Record<EnumNotificationRelatedObjectType, RouteBuilder> = {
    [EnumNotificationRelatedObjectType.Order]: (id) => `/orders/${id}`,
    [EnumNotificationRelatedObjectType.Invoice]: (id) => `/billing/invoices/${id}`,
    [EnumNotificationRelatedObjectType.InvoiceSnapshot]: (id) => `/invoices/${id}`,
    [EnumNotificationRelatedObjectType.Requisition]: (id) => `/requisitions/${id}`,
    [EnumNotificationRelatedObjectType.Archive]: (id) => `/downloads?open=${id}`,
    [EnumNotificationRelatedObjectType.DynamicExportRun]: (id) => `/settings/export_runs?open=${id}`,
};

/** The route a notification opens, relative to the mandant subpath, or undefined when its type
 * has none or its record is unknown. */
// @ai-generated 2026-09-02T00:00:00+00:00 by philipp.kammerer@synqony.com using claude-opus-5
export function notificationLinkPath(notification: ApiNotification): string | undefined {
    const id = notification.related_object_id;

    // Points at an order, but the operator works a new one off in the intake, not in its detail view.
    if (notification.key === NotificationTypeKeys.requisition_received_operator) {
        return id === undefined ? '/orders/list?mode=staging' : `/orders/list?mode=staging&open=${id}`;
    }

    // A type this bundle does not know means a newer backend, so the key below is the better guess.
    const route: RouteBuilder | undefined =
        notification.related_object_type === undefined
            ? undefined
            : ROUTE_BY_RELATED_OBJECT_TYPE[notification.related_object_type];
    if (id !== undefined && route !== undefined) {
        return route(id);
    }

    // Responses cached before the record type was carried along still route by their key alone.
    switch (notification.key) {
        case NotificationTypeKeys.requisition_status_changed:
            return id === undefined ? undefined : `/requisitions/${id}`;
        case NotificationTypeKeys.invoice_received:
            return id === undefined ? undefined : `/invoices/${id}`;
        case NotificationTypeKeys.email_not_delivered:
            return id === undefined ? undefined : `/billing/invoices/${id}`;
        default:
            return undefined;
    }
}

/** Type keys a user cannot switch off in the app, sourced from the backend's machine-readable list. */
const MANDATORY_IN_APP_TYPE_KEYS: ReadonlySet<string> = new Set(Object.values(NotificationMandatoryInAppTypeKeys));

/** Whether the In-App channel of this type cannot be switched off. */
// @ai-generated 2026-09-28T14:04:37+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function isMandatoryInApp(typeKey: string | undefined): boolean {
    return typeKey !== undefined && MANDATORY_IN_APP_TYPE_KEYS.has(typeKey);
}

/** Whether the module row's In-App switch is fixed, because every type of the module is mandatory in the app. */
// @ai-generated 2026-09-28T14:04:37+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function isModuleMandatoryInApp(moduleEntry: NotificationCatalogModule): boolean {
    return moduleEntry.types.length > 0 && moduleEntry.types.every((type) => isMandatoryInApp(type.typeKey));
}

/** Resolves the effective In-App/Email state for a (module, typeKey) pair; a type-level override
 * wins over the module-level default, and a mandatory in-app type is always on in the app. */
export function resolveChannels(
    settings: ApiNotificationSetting[],
    module: EnumNotificationModule,
    typeKey: string | undefined,
) {
    const typeOverride = typeKey !== undefined && settings.find((s) => s.module === module && s.key === typeKey);
    const mandatoryInApp = isMandatoryInApp(typeKey);
    if (typeOverride) {
        return { inApp: mandatoryInApp || typeOverride.in_app, email: typeOverride.email };
    }

    const moduleDefault = settings.find((s) => s.module === module && s.key === undefined);
    if (moduleDefault) {
        return { inApp: mandatoryInApp || moduleDefault.in_app, email: moduleDefault.email };
    }

    const emailDefault =
        typeKey !== undefined
            ? EMAIL_TEMPLATE_TYPE_KEYS.has(typeKey)
            : NOTIFICATION_CATALOG.some(
                  (moduleEntry) => moduleEntry.module === module && isModuleEmailAvailable(moduleEntry),
              );
    return { inApp: true, email: emailDefault };
}

/** The state a module's row shows: a channel counts as on as soon as one of the module's types has
 * it on, where In-App only counts switchable types. A module without types in the current catalog
 * falls back to its own default. */
// @ai-generated 2026-08-27T09:44:03+00:00 by philipp.kammerer@synqony.com using claude-opus-5
export function resolveModuleChannels(settings: ApiNotificationSetting[], moduleEntry: NotificationCatalogModule) {
    if (moduleEntry.types.length === 0) {
        return resolveChannels(settings, moduleEntry.module, undefined);
    }

    return {
        inApp:
            isModuleMandatoryInApp(moduleEntry) ||
            moduleEntry.types.some(
                (type) =>
                    !isMandatoryInApp(type.typeKey) &&
                    resolveChannels(settings, moduleEntry.module, type.typeKey).inApp,
            ),
        email: moduleEntry.types.some(
            (type) =>
                EMAIL_TEMPLATE_TYPE_KEYS.has(type.typeKey) &&
                resolveChannels(settings, moduleEntry.module, type.typeKey).email,
        ),
    };
}

export interface NotificationCatalogEntry {
    typeKey: string;
    labelDe: string;
}

export interface NotificationCatalogModule {
    module: EnumNotificationModule;
    labelDe: string;
    types: NotificationCatalogEntry[];
}

export const NOTIFICATION_CATALOG: NotificationCatalogModule[] = [
    {
        module: EnumNotificationModule.Billing,
        labelDe: 'Faktura',
        types: [
            { typeKey: NotificationTypeKeys.invoice_batch_failed, labelDe: 'Rechnungserstellung fehlgeschlagen' },
            { typeKey: NotificationTypeKeys.invoice_received, labelDe: 'Rechnungseingang' },
        ],
    },
    {
        module: EnumNotificationModule.Requisitions,
        labelDe: 'Bestellungen',
        types: [
            { typeKey: NotificationTypeKeys.requisition_status_changed, labelDe: 'Bestellstatus geändert' },
            {
                typeKey: NotificationTypeKeys.requisition_received_operator,
                labelDe: 'Auftrag zu Bestellung angelegt (Betreiber)',
            },
        ],
    },
    {
        module: EnumNotificationModule.Exports,
        labelDe: 'Exporte',
        types: [
            { typeKey: NotificationTypeKeys.dynamic_export_succeeded, labelDe: 'Export abgeschlossen' },
            { typeKey: NotificationTypeKeys.dynamic_export_failed, labelDe: 'Export fehlgeschlagen' },
            { typeKey: NotificationTypeKeys.archive_ready, labelDe: 'Download bereit' },
            { typeKey: NotificationTypeKeys.archive_failed, labelDe: 'Download fehlgeschlagen' },
        ],
    },
    {
        module: EnumNotificationModule.System,
        labelDe: 'Systemverwaltung',
        types: [{ typeKey: NotificationTypeKeys.external_app_secret_expiry, labelDe: 'Anwendungsgeheimnis läuft ab' }],
    },
    {
        module: EnumNotificationModule.Documents,
        labelDe: 'Dokumente',
        types: [{ typeKey: NotificationTypeKeys.attachment_upload_failed, labelDe: 'Dateiupload fehlgeschlagen' }],
    },
    {
        module: EnumNotificationModule.Mailing,
        labelDe: 'E-Mail',
        types: [{ typeKey: NotificationTypeKeys.email_not_delivered, labelDe: 'E-Mail nicht zugestellt' }],
    },
];

/** Type keys receivable per partnership type, sourced from the backend's machine-readable lists. */
const PORTAL_TYPE_KEYS: ReadonlySet<string> = new Set(Object.values(NotificationPortalTypeKeys));
const INTERNAL_TYPE_KEYS: ReadonlySet<string> = new Set(Object.values(NotificationInternalTypeKeys));

/** The catalog reduced to the types the given partnership type can receive. */
export function getCatalogForPartnershipType(partnershipType: EnumPartnershipType): NotificationCatalogModule[] {
    const receivable = partnershipType === EnumPartnershipType.Portal ? PORTAL_TYPE_KEYS : INTERNAL_TYPE_KEYS;
    return NOTIFICATION_CATALOG.map((moduleEntry) => ({
        ...moduleEntry,
        types: moduleEntry.types.filter((type) => receivable.has(type.typeKey)),
    })).filter((moduleEntry) => moduleEntry.types.length > 0);
}

/** Type keys with a registered email template, sourced from the backend's machine-readable list. */
export const EMAIL_TEMPLATE_TYPE_KEYS: ReadonlySet<string> = new Set(Object.values(NotificationEmailTypeKeys));

/** Whether the email channel is offered for a module (true when any of its types has an email template). */
export function isModuleEmailAvailable(moduleEntry: NotificationCatalogModule): boolean {
    return moduleEntry.types.some((type) => EMAIL_TEMPLATE_TYPE_KEYS.has(type.typeKey));
}
